<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  BookOpen,
  Download,
  Eye,
  FileText,
  History,
  House,
  LoaderCircle,
  Save,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  SquarePen,
  Star,
  Trash2,
} from '@lucide/vue'
import { DEFAULT_SETTINGS } from './app/constants'
import type { AppSettings, ParsedDocument, ReaderViewMode, ReadingProgress, RecentRecord, TableCellValue } from './app/types'
import ExportPanel from './components/ExportPanel.vue'
import FileDropZone from './components/FileDropZone.vue'
import JsonTree from './components/JsonTree.vue'
import LottiePreview from './components/LottiePreview.vue'
import SegmentedControl from './components/SegmentedControl.vue'
import StatusNotice from './components/StatusNotice.vue'
import {
  sortTableRows,
  switchSheet,
  updateTableCell,
  type TableSortDirection,
} from './editors/tableEditing'
import { createFileMeta, formatBytes } from './file-core/detect'
import { parseFile } from './parsers'
import { buildInteractiveSandboxSrcDoc, buildSandboxSrcDoc } from './security/sandbox'
import {
  clearLocalData,
  getDraft,
  getReadingProgress,
  listRecents,
  loadSettings,
  removeLocalRecord,
  saveDraft,
  saveReadingProgress,
  saveRecent,
  saveSettings,
  setRecentFavorite,
} from './storage/db'
import { findMatches, highlightText } from './utils/search'

type TabKey = 'files' | 'recent' | 'settings'

interface BusyState {
  label: string
  detail: string
}

interface TableRowView {
  row: TableCellValue[]
  index: number
}

interface OutlineItem {
  index: number
  level: number
  text: string
}

const activeTab = ref<TabKey>('files')
const currentDocument = ref<ParsedDocument | null>(null)
const htmlPreviewRef = ref<HTMLIFrameElement>()
const recents = ref<RecentRecord[]>([])
const settings = ref<AppSettings>({ ...DEFAULT_SETTINGS })
const isEditing = ref(false)
const showExport = ref(false)
const showDisplay = ref(false)
const showOutline = ref(false)
const htmlInteractivePreview = ref(false)
const query = ref('')
const filterColumn = ref<number | null>(null)
const filterValue = ref('')
const hideEmptyColumns = ref(false)
const sortColumn = ref<number | null>(null)
const sortDirection = ref<TableSortDirection>('asc')
const readingProgressPercent = ref(0)
const installPrompt = ref<BeforeInstallPromptEvent | null>(null)
const busy = ref<BusyState | null>(null)
const toast = ref('')
const error = ref('')
let draftTimer: number | undefined
let readingProgressTimer: number | undefined
let editSequence = 0

const searchMatches = computed(() => {
  if (!currentDocument.value) {
    return []
  }

  return findMatches(currentDocument.value.textContent, query.value)
})

const currentSheet = computed(() => {
  const doc = currentDocument.value
  return doc?.sheets?.[doc.activeSheetIndex ?? 0]
})

const outlineItems = computed<OutlineItem[]>(() => {
  const document = currentDocument.value
  const html = document?.safeHtml
  if (!html || !['preview', 'document'].includes(document.viewMode) || (document.kind === 'html' && htmlInteractivePreview.value)) {
    return []
  }

  const parsed = new DOMParser().parseFromString(html, 'text/html')
  return Array.from(parsed.querySelectorAll('h1, h2, h3'))
    .map((heading, index) => ({
      index,
      level: Number(heading.tagName.slice(1)),
      text: heading.textContent?.trim() ?? '',
    }))
    .filter((item) => item.text)
})

const shownColumnIndexes = computed(() => {
  const sheet = currentSheet.value
  if (!sheet) {
    return []
  }

  const indexes = sheet.headers.map((_, index) => index)
  if (!hideEmptyColumns.value) {
    return indexes
  }

  const rows = filteredRows.value.length ? filteredRows.value.map((item) => item.row) : sheet.rows
  const visible = indexes.filter((index) =>
    rows.some((row) => row[index] !== null && String(row[index] ?? '').trim() !== ''),
  )
  return visible.length ? visible : indexes.slice(0, 1)
})

const filteredRows = computed<TableRowView[]>(() => {
  const sheet = currentSheet.value
  const normalizedQuery = query.value.trim().toLowerCase()
  const normalizedFilter = filterValue.value.trim().toLowerCase()

  if (!sheet) {
    return []
  }

  return sheet.rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => {
      const matchesGlobal = !normalizedQuery || row.join(' ').toLowerCase().includes(normalizedQuery)
      const matchesColumn = !normalizedFilter || filterColumn.value === null
        ? true
        : String(row[filterColumn.value] ?? '').toLowerCase().includes(normalizedFilter)
      return matchesGlobal && matchesColumn
    })
})

const visibleRows = computed<TableRowView[]>(() => {
  const rows = sortColumn.value === null
    ? filteredRows.value
    : sortTableRows(filteredRows.value, sortColumn.value, sortDirection.value)

  return rows.slice(0, 250)
})

const filteredExportDocument = computed<ParsedDocument | undefined>(() => {
  const document = currentDocument.value
  const sheet = currentSheet.value
  if (!document || !sheet) {
    return undefined
  }

  const hasFiltering = Boolean(query.value.trim() || filterValue.value.trim() || hideEmptyColumns.value || sortColumn.value !== null)
  if (!hasFiltering) {
    return undefined
  }

  const rows = sortColumn.value === null
    ? filteredRows.value
    : sortTableRows(filteredRows.value, sortColumn.value, sortDirection.value)
  const headers = shownColumnIndexes.value.map((index) => sheet.headers[index])
  const exportedRows = rows.map(({ row }) => shownColumnIndexes.value.map((index) => row[index] ?? ''))
  const exportedSheet = {
    ...sheet,
    headers,
    rows: exportedRows,
    rowCount: exportedRows.length,
    colCount: headers.length,
  }

  return { ...document, sheets: [exportedSheet], activeSheetIndex: 0 }
})

const viewOptions = computed(() => currentDocument.value ? getViewOptions(currentDocument.value) : [])

const recentPreview = computed(() => recents.value.slice(0, 5))

onMounted(async () => {
  window.addEventListener('scroll', handleReaderScroll, { passive: true })
  window.addEventListener('pagehide', persistCurrentReadingProgress)
  window.addEventListener('beforeinstallprompt', captureInstallPrompt)
  window.addEventListener('appinstalled', handleAppInstalled)
  registerFileLaunchHandler()
  settings.value = await loadSettings()
  applyTheme(settings.value)
  recents.value = await listRecents()
})

onBeforeUnmount(() => {
  persistCurrentReadingProgress()
  releasePdfObjectUrl(currentDocument.value)
  window.removeEventListener('scroll', handleReaderScroll)
  window.removeEventListener('pagehide', persistCurrentReadingProgress)
  window.removeEventListener('beforeinstallprompt', captureInstallPrompt)
  window.removeEventListener('appinstalled', handleAppInstalled)
  window.clearTimeout(draftTimer)
  window.clearTimeout(readingProgressTimer)
})

watch(
  settings,
  (value) => {
    applyTheme(value)
    void saveSettings(value)
  },
  { deep: true },
)

watch(
  currentDocument,
  (document) => {
    if (!document) {
      return
    }

    window.clearTimeout(draftTimer)
    draftTimer = window.setTimeout(async () => {
      await saveRecent(document)
      if (document.kind !== 'pdf') {
        await saveDraft(document)
      }
      recents.value = await listRecents()
    }, 700)
  },
  { deep: true },
)

async function activateDocument(sourceDocument: ParsedDocument) {
  const progress = await getReadingProgress(sourceDocument.id)
  releasePdfObjectUrl(currentDocument.value)
  const supportedModes = new Set(getViewOptions(sourceDocument).map((option) => option.value))
  const activeSheetIndex = progress?.activeSheetIndex !== undefined && sourceDocument.sheets?.[progress.activeSheetIndex]
    ? progress.activeSheetIndex
    : sourceDocument.activeSheetIndex

  resetTableTools()
  showOutline.value = false
  currentDocument.value = {
    ...sourceDocument,
    viewMode: progress && supportedModes.has(progress.viewMode)
      ? progress.viewMode
      : sourceDocument.viewMode,
    activeSheetIndex,
  }
  activeTab.value = 'files'

  await nextTick()
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: progress?.scrollTop ?? 0, behavior: 'auto' })
      updateReadingProgressPercent()
    })
  })
}

async function handleFile(file: File) {
  editSequence += 1
  error.value = ''
  toast.value = ''
  query.value = ''
  showDisplay.value = false
  showExport.value = false
  htmlInteractivePreview.value = false
  showOutline.value = false
  isEditing.value = false

  try {
    busy.value = { label: '读取文件', detail: file.name }
    const meta = await createFileMeta(file)

    if (meta.kind === 'unknown') {
      throw new Error('暂不支持该格式。请使用 DOCX、XLSX、PDF、Markdown、HTML、CSV、JSON、Lottie 或 TXT。')
    }

    const existingDraft = meta.kind === 'pdf' ? undefined : await getDraft(meta.id)
    if (existingDraft) {
      await activateDocument(existingDraft.document)
      htmlInteractivePreview.value = false
      toast.value = '已恢复此文件的本机草稿和上次阅读位置。'
      busy.value = null
      return
    }

    const parsed = await parseFile(file, {
      meta,
      settings: settings.value,
      report: (_stage, message) => {
        busy.value = { label: message, detail: file.name }
      },
    })

    await activateDocument(parsed)
    htmlInteractivePreview.value = false
    await saveRecent(parsed)
    recents.value = await listRecents()
  } catch (exception) {
    error.value = exception instanceof Error ? exception.message : '打开文件失败'
  } finally {
    busy.value = null
  }
}

async function openRecent(record: RecentRecord) {
  editSequence += 1
  const draft = await getDraft(record.id)
  if (!draft) {
    error.value = record.kind === 'pdf'
      ? 'PDF 原文件不会写入本机草稿，请重新选择该 PDF 打开。'
      : '出于隐私设计，轻阅档不保存原文件内容。请重新选择本机文件打开。'
    return
  }

  await activateDocument(draft.document)
  query.value = ''
  isEditing.value = false
  htmlInteractivePreview.value = false
  toast.value = '已打开本机草稿并恢复阅读位置。'
}

async function toggleFavorite(record: RecentRecord) {
  const favorite = !record.favorite
  await setRecentFavorite(record.id, favorite)
  recents.value = await listRecents()
  toast.value = favorite ? '已收藏并置顶该文件。' : '已取消收藏。'
}

async function removeRecord(id: string) {
  await removeLocalRecord(id)
  recents.value = await listRecents()
  if (currentDocument.value?.id === id) {
    window.clearTimeout(draftTimer)
    releasePdfObjectUrl(currentDocument.value)
    editSequence += 1
    currentDocument.value = null
  }
}

async function clearAllData() {
  if (!window.confirm('清除最近记录和全部草稿？此操作不会删除你的原文件。')) {
    return
  }

  window.clearTimeout(draftTimer)
  releasePdfObjectUrl(currentDocument.value)
  editSequence += 1
  await clearLocalData()
  recents.value = []
  currentDocument.value = null
  toast.value = '本机记录和草稿已清除。'
}

function closeReader() {
  persistCurrentReadingProgress()
  releasePdfObjectUrl(currentDocument.value)
  editSequence += 1
  currentDocument.value = null
  isEditing.value = false
  showExport.value = false
  showDisplay.value = false
  htmlInteractivePreview.value = false
  showOutline.value = false
  query.value = ''
  readingProgressPercent.value = 0
  resetTableTools()
  window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }))
}

function setViewMode(value: string) {
  if (!currentDocument.value) {
    return
  }

  currentDocument.value = {
    ...currentDocument.value,
    viewMode: value as ReaderViewMode,
  }
  persistCurrentReadingProgress()
}

function toggleEditing() {
  if (!currentDocument.value) {
    return
  }

  isEditing.value = !isEditing.value

  if (isEditing.value && ['md', 'html', 'json', 'lottie', 'docx', 'txt'].includes(currentDocument.value.kind)) {
    setViewMode(currentDocument.value.kind === 'txt' ? 'text' : 'source')
  }

  if (!isEditing.value) {
    toast.value = '草稿已自动保存在本机。'
  }
}

async function updateEditableText(value: string) {
  const document = currentDocument.value
  if (!document) {
    return
  }

  const documentId = document.id
  currentDocument.value = {
    ...document,
    editableText: value,
    rawText: value,
    textContent: value,
  }

  const sequence = ++editSequence
  const { updateSourceDocument } = await import('./editors/documentEditing')

  if (sequence === editSequence && currentDocument.value?.id === documentId) {
    currentDocument.value = updateSourceDocument(currentDocument.value, value, settings.value)
  }
}

function updateCell(rowIndex: number, colIndex: number, value: string) {
  const doc = currentDocument.value
  if (!doc) {
    return
  }

  currentDocument.value = updateTableCell(doc, doc.activeSheetIndex ?? 0, rowIndex, colIndex, value)
}

function selectSheet(index: number) {
  if (!currentDocument.value) {
    return
  }

  currentDocument.value = switchSheet(currentDocument.value, index)
  resetTableTools()
  persistCurrentReadingProgress()
}

function setSortColumn(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  sortColumn.value = value === '' ? null : Number(value)
  sortDirection.value = 'asc'
}

function toggleSortColumn(index: number) {
  if (sortColumn.value === index) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    return
  }

  sortColumn.value = index
  sortDirection.value = 'asc'
}

function toggleSortDirection() {
  if (sortColumn.value === null) {
    return
  }

  sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
}

function resetTableSort() {
  sortColumn.value = null
  sortDirection.value = 'asc'
}

function resetTableTools() {
  resetTableSort()
  filterColumn.value = null
  filterValue.value = ''
  hideEmptyColumns.value = false
}

function setFilterColumn(event: Event) {
  const value = (event.target as HTMLSelectElement).value
  filterColumn.value = value === '' ? null : Number(value)
  if (filterColumn.value === null) {
    filterValue.value = ''
  }
}

function clearTableFilters() {
  query.value = ''
  filterColumn.value = null
  filterValue.value = ''
  hideEmptyColumns.value = false
  resetTableSort()
}

function tableCellHighlight(cell: TableCellValue, columnIndex: number): string {
  const highlightQuery = query.value.trim() || (filterColumn.value === columnIndex ? filterValue.value.trim() : '')
  return highlightText(String(cell ?? ''), highlightQuery)
}

function tableAriaSort(index: number): 'ascending' | 'descending' | 'none' {
  if (sortColumn.value !== index) {
    return 'none'
  }

  return sortDirection.value === 'asc' ? 'ascending' : 'descending'
}

function handleReaderScroll() {
  if (!currentDocument.value) {
    return
  }

  updateReadingProgressPercent()
  window.clearTimeout(readingProgressTimer)
  readingProgressTimer = window.setTimeout(persistCurrentReadingProgress, 220)
}

function updateReadingProgressPercent() {
  if (!currentDocument.value) {
    readingProgressPercent.value = 0
    return
  }

  const maximum = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
  readingProgressPercent.value = maximum === 0
    ? 100
    : Math.min(100, Math.max(0, (window.scrollY / maximum) * 100))
}

function persistCurrentReadingProgress() {
  const activeDocument = currentDocument.value
  if (!activeDocument) {
    return
  }

  const progress: ReadingProgress = {
    id: activeDocument.id,
    scrollTop: Math.max(0, window.scrollY),
    viewMode: activeDocument.viewMode,
    activeSheetIndex: activeDocument.activeSheetIndex,
    updatedAt: Date.now(),
  }

  void saveReadingProgress(progress)
}

function scrollToOutlineItem(index: number) {
  if (currentDocument.value?.kind === 'html') {
    const headings = htmlPreviewRef.value?.contentDocument?.querySelectorAll<HTMLElement>('h1, h2, h3')
    headings?.[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  } else {
    const headings = document.querySelectorAll<HTMLElement>('.rich-content h1, .rich-content h2, .rich-content h3')
    headings[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
  showOutline.value = false
}

function captureInstallPrompt(event: Event) {
  event.preventDefault()
  installPrompt.value = event as BeforeInstallPromptEvent
}

function handleAppInstalled() {
  installPrompt.value = null
  toast.value = '轻阅档已安装，可从系统文件管理器直接打开支持的文件。'
}

async function installApp() {
  const prompt = installPrompt.value
  if (!prompt) {
    return
  }

  await prompt.prompt()
  const choice = await prompt.userChoice
  if (choice.outcome === 'dismissed') {
    toast.value = '已取消安装，可稍后从浏览器菜单再次安装。'
  }
  installPrompt.value = null
}

function registerFileLaunchHandler() {
  window.launchQueue?.setConsumer(async (params) => {
    const handle = params.files?.[0]
    if (!handle) {
      return
    }

    try {
      await handleFile(await handle.getFile())
    } catch (exception) {
      error.value = exception instanceof Error ? exception.message : '无法从系统打开该文件'
    }
  })
}

function releasePdfObjectUrl(documentValue: ParsedDocument | null) {
  if (documentValue?.pdfObjectUrl) {
    URL.revokeObjectURL(documentValue.pdfObjectUrl)
  }
}

function copySearchPreview(preview: string) {
  void navigator.clipboard?.writeText(preview)
  toast.value = '已复制命中片段。'
}

function getViewOptions(document: ParsedDocument): Array<{ value: ReaderViewMode; label: string }> {
  if (document.kind === 'pdf') {
    return [{ value: 'pdf', label: 'PDF' }]
  }

  if (document.kind === 'xlsx' || document.kind === 'csv') {
    return [
      { value: 'cards', label: '卡片' },
      { value: 'table', label: '表格' },
    ]
  }

  if (document.kind === 'md') {
    return [
      { value: 'preview', label: '预览' },
      { value: 'source', label: '源码' },
    ]
  }

  if (document.kind === 'html') {
    return [
      { value: 'preview', label: '预览' },
      { value: 'source', label: '源码' },
      { value: 'text', label: '文本' },
    ]
  }

  if (document.kind === 'json' || document.kind === 'lottie') {
    return [
      ...(document.jsonAnimation ? [{ value: 'animation' as const, label: '动画' }] : []),
      { value: 'tree', label: '树' },
      { value: 'source', label: '源码' },
    ]
  }

  if (document.kind === 'docx') {
    return [
      { value: 'document', label: '阅读' },
      { value: 'source', label: '文字' },
    ]
  }

  return [
    { value: 'text', label: '阅读' },
    { value: 'source', label: '编辑' },
  ]
}

function applyTheme(value: AppSettings) {
  const root = document.documentElement
  if (value.theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.dataset.theme = value.theme
  }
}

function formatKind(kind: string): string {
  return kind.toUpperCase()
}
</script>

<template>
  <div class="app-shell">
    <header class="app-header">
      <div class="brand-lockup">
        <span class="brand-mark" aria-hidden="true">
          <FileText :size="22" />
        </span>
        <div>
          <p class="brand-name">轻阅档</p>
          <p class="brand-subtitle">纯前端本地文件阅读与轻编辑</p>
        </div>
      </div>
      <span class="local-badge">
        <ShieldCheck :size="16" aria-hidden="true" />
        无服务器
      </span>
    </header>

    <main v-if="!currentDocument" class="home-shell">
      <section v-if="activeTab === 'files'" class="page-stack">
        <FileDropZone @select="handleFile" />

        <section class="panel" aria-labelledby="recent-title">
          <header class="section-head">
            <div>
              <p class="eyebrow">最近</p>
              <h2 id="recent-title">本机记录</h2>
            </div>
            <button class="text-button" type="button" @click="activeTab = 'recent'">查看全部</button>
          </header>

          <div v-if="recentPreview.length" class="recent-list compact">
            <button
              v-for="record in recentPreview"
              :key="record.id"
              class="recent-row"
              type="button"
              @click="openRecent(record)"
            >
              <span class="file-pill">{{ formatKind(record.kind) }}</span>
              <span class="recent-copy">
                <strong>
                  {{ record.name }}
                  <Star v-if="record.favorite" class="favorite-inline" :size="14" fill="currentColor" aria-label="已收藏" />
                </strong>
                <small>{{ formatBytes(record.size) }} · {{ record.draftUpdatedAt ? '有草稿' : '最近打开' }}</small>
              </span>
            </button>
          </div>
          <StatusNotice v-else>
            最近和草稿只保存在当前浏览器。重新打开原文件时，可恢复同一文件的本机草稿。
          </StatusNotice>
        </section>
      </section>

      <section v-else-if="activeTab === 'recent'" class="page-stack">
        <section class="panel">
          <header class="section-head">
            <div>
              <p class="eyebrow">最近与草稿</p>
              <h1>本机文件记录</h1>
            </div>
            <button class="danger-button" type="button" :disabled="!recents.length" @click="clearAllData">
              <Trash2 :size="18" aria-hidden="true" />
              全部清除
            </button>
          </header>

          <div v-if="recents.length" class="recent-list">
            <article v-for="record in recents" :key="record.id" class="record-card" :class="{ favorite: record.favorite }">
              <button class="record-main" type="button" @click="openRecent(record)">
                <span class="file-pill">{{ formatKind(record.kind) }}</span>
                <span class="recent-copy">
                  <strong>{{ record.name }}</strong>
                  <small>{{ formatBytes(record.size) }} · {{ record.draftUpdatedAt ? '草稿已保存' : '仅元数据' }}</small>
                </span>
              </button>
              <div class="record-actions">
                <button
                  class="icon-button favorite-button"
                  :class="{ active: record.favorite }"
                  type="button"
                  :aria-label="record.favorite ? '取消收藏' : '收藏并置顶'"
                  :aria-pressed="Boolean(record.favorite)"
                  @click="toggleFavorite(record)"
                >
                  <Star :size="18" :fill="record.favorite ? 'currentColor' : 'none'" aria-hidden="true" />
                </button>
                <button class="icon-button" type="button" aria-label="删除记录" @click="removeRecord(record.id)">
                  <Trash2 :size="18" aria-hidden="true" />
                </button>
              </div>
            </article>
          </div>

          <StatusNotice v-else>当前没有最近记录。选择一个文件后，这里会显示本机元数据和草稿状态。</StatusNotice>
        </section>
      </section>

      <section v-else class="page-stack">
        <section class="panel settings-panel" aria-labelledby="settings-title">
          <header class="section-head">
            <div>
              <p class="eyebrow">设置</p>
              <h1 id="settings-title">阅读偏好与隐私</h1>
            </div>
          </header>

          <label class="setting-row">
            <span>
              <strong>主题</strong>
              <small>跟随系统或固定浅色/深色</small>
            </span>
            <select v-model="settings.theme">
              <option value="system">跟随系统</option>
              <option value="light">浅色</option>
              <option value="dark">深色</option>
            </select>
          </label>

          <label class="setting-row">
            <span>
              <strong>默认字号</strong>
              <small>{{ Math.round(settings.fontScale * 100) }}%</small>
            </span>
            <input v-model.number="settings.fontScale" type="range" min="0.9" max="1.25" step="0.05" />
          </label>

          <label class="setting-row">
            <span>
              <strong>表格密度</strong>
              <small>影响手机表格和卡片间距</small>
            </span>
            <select v-model="settings.tableDensity">
              <option value="comfortable">舒适</option>
              <option value="compact">紧凑</option>
            </select>
          </label>

          <label class="setting-row">
            <span>
              <strong>远程图片</strong>
              <small>默认阻止导入内容加载外部资源</small>
            </span>
            <input v-model="settings.loadRemoteImages" type="checkbox" />
          </label>

          <StatusNotice v-if="installPrompt">
            安装为应用后，可从支持的手机或电脑文件管理器选择“用轻阅档打开”。
          </StatusNotice>

          <button v-if="installPrompt" class="primary-action full" type="button" @click="installApp">
            <Download :size="19" aria-hidden="true" />
            安装轻阅档
          </button>

          <StatusNotice>
            文件内容、草稿、收藏和阅读位置仅保存在当前浏览器。Service Worker 只缓存应用静态资源，不默认缓存用户导入文件。
          </StatusNotice>

          <button class="danger-button full" type="button" @click="clearAllData">
            <Trash2 :size="18" aria-hidden="true" />
            清除本机记录与草稿
          </button>
        </section>
      </section>
    </main>

    <main v-else class="reader-shell">
      <header class="reader-header">
        <button class="icon-button" type="button" aria-label="返回首页" @click="closeReader">
          <ArrowLeft :size="20" aria-hidden="true" />
        </button>
        <div class="reader-title">
          <p>{{ currentDocument.meta.name }}</p>
          <small>{{ formatKind(currentDocument.kind) }} · {{ formatBytes(currentDocument.meta.size) }}</small>
        </div>
        <div class="reader-header-actions">
          <button
            v-if="outlineItems.length"
            class="icon-button"
            type="button"
            :class="{ active: showOutline }"
            aria-label="打开文档目录"
            :aria-expanded="showOutline"
            @click="showOutline = !showOutline"
          >
            <BookOpen :size="20" aria-hidden="true" />
          </button>
          <button v-if="currentDocument.kind !== 'pdf'" class="icon-button" type="button" aria-label="导出" @click="showExport = true">
            <Download :size="20" aria-hidden="true" />
          </button>
        </div>
        <div class="reading-progress" aria-hidden="true">
          <span :style="{ width: `${readingProgressPercent}%` }" />
        </div>
      </header>

      <section class="reader-body" :style="{ '--reader-scale': settings.fontScale }">
        <aside v-if="showOutline && outlineItems.length" class="outline-panel" aria-label="文档目录">
          <header>
            <div>
              <p class="eyebrow">目录</p>
              <h2>快速跳转</h2>
            </div>
            <button class="text-button" type="button" @click="showOutline = false">收起</button>
          </header>
          <nav>
            <button
              v-for="item in outlineItems"
              :key="`${item.index}-${item.text}`"
              type="button"
              :style="{ '--outline-level': item.level }"
              @click="scrollToOutlineItem(item.index)"
            >
              {{ item.text }}
            </button>
          </nav>
        </aside>

        <div v-if="currentDocument.warnings.length" class="warning-stack">
          <StatusNotice
            v-for="warning in currentDocument.warnings.slice(0, 3)"
            :key="warning"
            tone="warning"
          >
            {{ warning }}
          </StatusNotice>
        </div>

        <div v-if="searchMatches.length" class="search-results" aria-label="搜索结果">
          <button
            v-for="match in searchMatches.slice(0, 6)"
            :key="match.index"
            type="button"
            @click="copySearchPreview(match.preview)"
          >
            <Search :size="15" aria-hidden="true" />
            <span>{{ match.preview }}</span>
          </button>
        </div>

        <div v-if="currentDocument.sheets?.length" class="sheet-tabs" role="tablist" aria-label="工作表">
          <button
            v-for="(sheet, index) in currentDocument.sheets"
            :key="`${index}-${sheet.name}`"
            type="button"
            :class="{ active: (currentDocument.activeSheetIndex ?? 0) === index }"
            @click="selectSheet(index)"
          >
            {{ sheet.name }}
          </button>
        </div>

        <section v-if="currentSheet" class="table-tools" aria-label="表格工具">
          <div class="table-summary">
            <strong>{{ filteredRows.length }}</strong>
            <span>条结果 · {{ shownColumnIndexes.length }} 列，共 {{ currentSheet.rows.length }} 行</span>
          </div>

          <div class="table-tool-grid">
            <label class="table-control">
              <span>筛选列</span>
              <select :value="filterColumn ?? ''" @change="setFilterColumn">
                <option value="">全部列</option>
                <option v-for="(header, index) in currentSheet.headers" :key="`filter-${index}-${header}`" :value="index">
                  {{ header }}
                </option>
              </select>
            </label>

            <label class="table-control table-filter-input">
              <span>包含内容</span>
              <input v-model="filterValue" type="search" :disabled="filterColumn === null" placeholder="输入筛选文字" />
            </label>

            <label class="table-control">
              <span>排序列</span>
              <select :value="sortColumn ?? ''" @change="setSortColumn">
                <option value="">原始顺序</option>
                <option v-for="(header, index) in currentSheet.headers" :key="`sort-${index}-${header}`" :value="index">
                  {{ header }}
                </option>
              </select>
            </label>

            <button
              class="sort-direction-button"
              type="button"
              :disabled="sortColumn === null"
              :aria-label="sortDirection === 'asc' ? '切换为降序' : '切换为升序'"
              @click="toggleSortDirection"
            >
              <ArrowUp v-if="sortDirection === 'asc'" :size="17" aria-hidden="true" />
              <ArrowDown v-else :size="17" aria-hidden="true" />
              {{ sortDirection === 'asc' ? '升序' : '降序' }}
            </button>
          </div>

          <div class="table-tool-footer">
            <label class="inline-check">
              <input v-model="hideEmptyColumns" type="checkbox" />
              隐藏空列
            </label>
            <span class="table-auto-width">列宽已按内容自动适配</span>
            <button
              v-if="query || filterValue || hideEmptyColumns || sortColumn !== null"
              class="text-button compact-button"
              type="button"
              @click="clearTableFilters"
            >
              重置
            </button>
          </div>
        </section>

        <article class="reader-surface" :class="[`kind-${currentDocument.kind}`, `mode-${currentDocument.viewMode}`, settings.tableDensity]">
          <template v-if="isEditing && ['txt', 'md', 'html', 'json', 'lottie', 'docx'].includes(currentDocument.kind)">
            <label class="editor-label" for="source-editor">源码或文字编辑</label>
            <textarea
              id="source-editor"
              class="source-editor"
              :value="currentDocument.editableText"
              spellcheck="false"
              @input="updateEditableText(($event.target as HTMLTextAreaElement).value)"
            />
            <StatusNotice v-if="currentDocument.jsonError" tone="danger">
              JSON 无效：{{ currentDocument.jsonError }}
            </StatusNotice>
          </template>

          <template v-else-if="currentDocument.sheets && currentSheet && currentDocument.viewMode === 'cards'">
            <div class="card-list">
              <article v-for="{ row, index } in visibleRows" :key="index" class="data-card">
                <h3>{{ String(row[0] || `第 ${index + 1} 行`) }}</h3>
                <dl>
                  <template v-for="colIndex in shownColumnIndexes" :key="`${colIndex}-${currentSheet.headers[colIndex]}`">
                    <dt>{{ currentSheet.headers[colIndex] }}</dt>
                    <dd>
                      <input
                        v-if="isEditing"
                        :value="String(row[colIndex] ?? '')"
                        @input="updateCell(index, colIndex, ($event.target as HTMLInputElement).value)"
                      />
                      <span v-else v-html="tableCellHighlight(row[colIndex], colIndex)" />
                    </dd>
                  </template>
                </dl>
              </article>
            </div>
          </template>

          <template v-else-if="currentDocument.sheets && currentSheet">
            <div class="table-wrap" tabindex="0" aria-label="表格内容，可横向滚动">
              <table>
                <thead>
                  <tr>
                    <th
                      v-for="colIndex in shownColumnIndexes"
                      :key="`${colIndex}-${currentSheet.headers[colIndex]}`"
                      :aria-sort="tableAriaSort(colIndex)"
                    >
                      <button class="table-sort-button" type="button" @click="toggleSortColumn(colIndex)">
                        <span>{{ currentSheet.headers[colIndex] }}</span>
                        <ArrowUp v-if="sortColumn === colIndex && sortDirection === 'asc'" :size="15" aria-hidden="true" />
                        <ArrowDown v-else-if="sortColumn === colIndex" :size="15" aria-hidden="true" />
                        <ArrowUpDown v-else :size="15" aria-hidden="true" />
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="{ row, index } in visibleRows" :key="index">
                    <td v-for="colIndex in shownColumnIndexes" :key="colIndex">
                      <input
                        v-if="isEditing"
                        :value="String(row[colIndex] ?? '')"
                        @input="updateCell(index, colIndex, ($event.target as HTMLInputElement).value)"
                      />
                      <span v-else v-html="tableCellHighlight(row[colIndex], colIndex)" />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="visibleRows.length < filteredRows.length" class="table-note">
              当前仅渲染前 250 条匹配结果以保持手机流畅，可继续缩小搜索范围。
            </p>
          </template>

          <template v-else-if="currentDocument.kind === 'pdf' && currentDocument.pdfObjectUrl">
            <section class="pdf-reader" aria-label="PDF 本地阅读器">
              <div class="pdf-reader-head">
                <StatusNotice>
                  PDF 正在使用浏览器内置阅读器本地打开。可使用阅读器自带的页码、缩放、下载和搜索功能。
                </StatusNotice>
                <a class="text-button" :href="currentDocument.pdfObjectUrl" target="_blank" rel="noopener">
                  在新窗口打开
                </a>
              </div>
              <iframe
                class="pdf-frame"
                title="PDF 阅读"
                :src="`${currentDocument.pdfObjectUrl}#toolbar=1&navpanes=0&view=FitH`"
              />
            </section>
          </template>

          <template v-else-if="(currentDocument.kind === 'json' || currentDocument.kind === 'lottie') && currentDocument.viewMode === 'tree' && currentDocument.jsonValue !== undefined">
            <JsonTree name="root" :value="currentDocument.jsonValue" />
          </template>

          <template v-else-if="(currentDocument.kind === 'json' || currentDocument.kind === 'lottie') && currentDocument.viewMode === 'animation' && currentDocument.jsonAnimation">
            <LottiePreview :document="currentDocument" />
          </template>

          <template v-else-if="currentDocument.viewMode === 'preview' && currentDocument.kind === 'html'">
            <div class="html-preview-tools">
              <StatusNotice :tone="htmlInteractivePreview ? 'warning' : 'info'">
                {{
                  htmlInteractivePreview
                    ? '已开启本次交互预览：脚本在 sandbox 中运行，网络请求、表单和父页面访问仍被限制。'
                    : '当前是安全预览：导入内容中的脚本已禁用。若这是小游戏或交互页面，可仅本次开启交互预览。'
                }}
              </StatusNotice>
              <button
                class="text-button"
                type="button"
                @click="htmlInteractivePreview = !htmlInteractivePreview"
              >
                {{ htmlInteractivePreview ? '返回安全预览' : '允许本次交互预览' }}
              </button>
            </div>
            <iframe
              ref="htmlPreviewRef"
              class="html-preview"
              :title="htmlInteractivePreview ? 'HTML 交互预览' : 'HTML 安全预览'"
              :sandbox="htmlInteractivePreview ? 'allow-scripts' : 'allow-same-origin'"
              :srcdoc="htmlInteractivePreview ? buildInteractiveSandboxSrcDoc(currentDocument.rawText) : buildSandboxSrcDoc(currentDocument.safeHtml ?? '')"
            />
          </template>

          <template v-else-if="['preview', 'document'].includes(currentDocument.viewMode) && currentDocument.safeHtml">
            <div class="rich-content" v-html="currentDocument.safeHtml" />
          </template>

          <template v-else>
            <pre class="plain-content" v-html="highlightText(currentDocument.editableText || currentDocument.textContent, query)" />
          </template>
        </article>
      </section>

      <section v-if="showDisplay" class="display-panel" aria-label="显示设置">
        <SegmentedControl
          v-if="viewOptions.length > 1"
          :model-value="currentDocument.viewMode"
          :options="viewOptions"
          label="视图切换"
          @update:model-value="setViewMode"
        />

        <label class="range-row">
          <span>字号</span>
          <input v-model.number="settings.fontScale" type="range" min="0.9" max="1.25" step="0.05" />
        </label>
      </section>

      <footer class="reader-toolbar" :class="{ 'pdf-toolbar': currentDocument.kind === 'pdf' }">
        <label v-if="currentDocument.kind !== 'pdf'" class="toolbar-search">
          <Search :size="18" aria-hidden="true" />
          <input v-model="query" type="search" placeholder="搜索内容" />
        </label>
        <div v-else class="toolbar-search pdf-toolbar-note">
          <Search :size="18" aria-hidden="true" />
          使用 PDF 阅读器内的搜索
        </div>
        <button type="button" @click="showDisplay = !showDisplay">
          <SlidersHorizontal :size="19" aria-hidden="true" />
          显示
        </button>
        <button v-if="currentDocument.kind !== 'pdf'" type="button" :class="{ active: isEditing }" @click="toggleEditing">
          <Save v-if="isEditing" :size="19" aria-hidden="true" />
          <SquarePen v-else :size="19" aria-hidden="true" />
          {{ isEditing ? '完成' : '编辑' }}
        </button>
        <button v-if="currentDocument.kind !== 'pdf'" type="button" @click="showExport = true">
          <Download :size="19" aria-hidden="true" />
          导出
        </button>
      </footer>

      <ExportPanel
        v-if="showExport"
        :document="currentDocument"
        :filtered-document="filteredExportDocument"
        :filtered-row-count="filteredRows.length"
        @close="showExport = false"
        @done="(message) => { toast = message; showExport = false }"
        @error="(message) => { error = message }"
      />
    </main>

    <nav class="bottom-nav" aria-label="主导航">
      <button type="button" :class="{ active: activeTab === 'files' && !currentDocument }" @click="activeTab = 'files'; closeReader()">
        <House :size="19" aria-hidden="true" />
        文件
      </button>
      <button type="button" :class="{ active: activeTab === 'recent' && !currentDocument }" @click="activeTab = 'recent'; closeReader()">
        <History :size="19" aria-hidden="true" />
        最近
      </button>
      <button type="button" :class="{ active: activeTab === 'settings' && !currentDocument }" @click="activeTab = 'settings'; closeReader()">
        <Settings :size="19" aria-hidden="true" />
        设置
      </button>
    </nav>

    <div v-if="busy" class="busy-layer" role="status" aria-live="polite">
      <LoaderCircle class="spin" :size="28" aria-hidden="true" />
      <strong>{{ busy.label }}</strong>
      <span>{{ busy.detail }}</span>
    </div>

    <div v-if="toast" class="toast success" role="status">
      <BookOpen :size="17" aria-hidden="true" />
      {{ toast }}
      <button type="button" aria-label="关闭提示" @click="toast = ''">×</button>
    </div>

    <div v-if="error" class="toast danger" role="alert">
      <Eye :size="17" aria-hidden="true" />
      {{ error }}
      <button type="button" aria-label="关闭错误" @click="error = ''">×</button>
    </div>
  </div>
</template>
